/**
 * Getting in: the calls the public pages make, and how their answers are read.
 * The backend answers by `code` (Auth `CodedError`), so a page shows one state
 * per code, never the server's sentence.
 */
import { isAxiosError } from "axios";
import { jwtDecode } from "jwt-decode";
import { axiosInstance } from "@/lib/api";

export type Session = { token: string; refreshToken?: string };

export type AuthFailure =
  | { kind: "offline" }
  | { kind: "rate"; waitSeconds: number }
  | { kind: "coded"; status: number; code: string | null; data: Record<string, unknown> };

/** Network error / 5xx → offline; 429 → rate (with the limiter's window); else the coded answer. */
export function readAuthFailure(error: unknown): AuthFailure {
  if (!isAxiosError(error) || !error.response || error.response.status >= 500) return { kind: "offline" };
  const { status, headers } = error.response;
  if (status === 429) {
    // express-rate-limit standard headers: RateLimit-Reset is seconds to go.
    const reset = Number(headers?.["ratelimit-reset"] ?? headers?.["retry-after"]);
    return { kind: "rate", waitSeconds: Number.isFinite(reset) && reset > 0 ? reset : 60 };
  }
  const data = (error.response.data ?? {}) as Record<string, unknown>;
  return { kind: "coded", status, code: typeof data.code === "string" ? data.code : null, data };
}

/** "a minute", "2 minutes": what the wait is called. */
export function waitWords(seconds: number): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return minutes === 1 ? "a minute" : `${minutes} minutes`;
}

/* ------------------------------------------------------------ destination */

/** Only an in-app path: never another origin (`//host`) or a scheme. */
export function safeRedirect(raw: string | null | undefined): string | null {
  return raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : null;
}

const DESTINATION_KEY = "co-auth-destination";

/** Keeps the destination across a hop out of the app (a verification email). */
export function rememberDestination(path: string | null) {
  try {
    if (path) sessionStorage.setItem(DESTINATION_KEY, path);
    else sessionStorage.removeItem(DESTINATION_KEY);
  } catch {
    /* storage off: the person lands on Home instead */
  }
}

export function takeDestination(): string | null {
  try {
    const value = safeRedirect(sessionStorage.getItem(DESTINATION_KEY));
    sessionStorage.removeItem(DESTINATION_KEY);
    return value;
  } catch {
    return null;
  }
}

/** The page a redirect path opens, in words (D5). The brand can't be read while signed out. */
export function destinationName(path: string): string | null {
  const p = path.split("?")[0];
  const pages: [RegExp, string][] = [
    [/^\/dashboard\/content-calendar/, "the calendar"],
    [/^\/dashboard\/workshop/, "the Workshop"],
    [/^\/dashboard\/brand-universe/, "the Universe"],
    [/^\/dashboard\/market-signals/, "Signals"],
    [/^\/dashboard\/strategy/, "the strategy"],
    [/^\/dashboard\/trends/, "Trends"],
    [/^\/dashboard\/references/, "References"],
    [/^\/dashboard\/settings/, "Settings"],
    [/^\/connect\/authorize/, "the connector you were allowing"],
  ];
  return pages.find(([re]) => re.test(p))?.[1] ?? null;
}

/* ------------------------------------------------------------ tokens */

/** The claims of a link token, read without checking it (the server checks). */
export function peekToken<T extends object>(token: string | null): (T & { exp?: number; iat?: number }) | null {
  if (!token) return null;
  try {
    return jwtDecode<T & { exp?: number; iat?: number }>(token);
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------ calls */

export const authCalls = {
  login: (email: string, password: string) =>
    axiosInstance.post<Session>("/auth/login", { email, password }, { skipAuthRefresh: true }).then((r) => r.data),
  google: (accessToken: string) =>
    axiosInstance
      .post<Session>("auth/oauth/login", { accessToken, provider: "GOOGLE" }, { skipAuthRefresh: true })
      .then((r) => r.data),
  register: (name: string, email: string, password: string) =>
    axiosInstance.post("/auth/register", { name, email, password }).then(() => undefined),
  verify: (token: string) => axiosInstance.post<Session>("auth/verify-email", { token }).then((r) => r.data),
  resendVerification: (email: string) =>
    axiosInstance.post("/auth/resend-verification-email", { email }).then(() => undefined),
  forgot: (email: string) => axiosInstance.post("/auth/forgot-password", { email }).then(() => undefined),
  inspectReset: (token: string) =>
    axiosInstance.post<{ email: string }>("/auth/password-reset/inspect", { token }).then((r) => r.data),
  reset: (token: string, password: string) =>
    axiosInstance.post<Session>("/auth/reset-password", { token, password }).then((r) => r.data),
};

/** The rule for every new password (D5), stated before typing. */
export const PASSWORD_MIN = 10;
export const PASSWORD_RULE = `At least ${PASSWORD_MIN} characters.`;
export function passwordRuleError(value: string): string | null {
  return value.length >= PASSWORD_MIN
    ? null
    : `At least ${PASSWORD_MIN} characters. This one has ${value.length}.`;
}

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
